<template>
  <div class="pm-analytics-chart-container" ref="table"></div>
</template>
<style lang="scss" scoped>
.pm-analytics-chart-container {
  width: 100%;
  height: 800px;
  font-weight: 600;
}
</style>
<style>
div.pm-analytics-chart-container > svg {
  height: 800px;
}
</style>
<script lang="ts">
import { select, Selection } from "d3-selection";
import { scaleLinear } from "d3-scale";
import { axisBottom, axisLeft } from "d3-axis";
import { Vue, Options } from "vue-class-component";
import { Ref, Prop } from "vue-property-decorator";
import { GetPMAnalytics, GetProfile } from "../../autogen";
import ActionButton from "./ActionButton.vue";

@Options({
  components: { ActionButton }
})
export default class PMAnalyticsChart extends Vue {
  @Prop({ type: Array, required: true }) chartData!: (GetPMAnalytics & GetProfile)[];
  @Prop({ type: Number }) size = 1;
  @Prop({ type: Boolean }) expand = false;
  @Prop({ type: Boolean }) zoom: "in" | "out" = "out";
  @Ref("table") table!: HTMLElement;
  svg!: Selection<SVGGElement, unknown, null, undefined>;
  /* eslint-disable */
  scaleLinearX: any;
  scaleLinearY: any;
  /* eslint-enable */

  draw() {
    const width = 900 * this.size;
    const height = 800 * this.size;
    const margin = { top: 15, right: 200, bottom: 100, left: 100 };
    const domainWidth = width - margin.left - margin.right;
    const domainHeight = height - margin.top - margin.bottom;
    const minScale = 20;
    const maxScale = 80;
    const crossLine = [
      {
        x1: 0,
        y1: domainHeight / 2,
        x2: domainWidth,
        y2: domainHeight / 2
      },
      {
        x1: domainWidth / 2,
        y1: domainHeight,
        x2: domainWidth / 2,
        y2: 0
      }
    ];
    const conditions = [
      {
        box: {
          color: "#D7EBFE",
          positionX: 0,
          positionY: 0,
          width: domainWidth / 2,
          height: domainHeight / 2
        },
        text: {
          label: "Pm",
          positionX: (domainWidth * 1) / 4,
          positionY: (domainHeight * 1) / 4 + 12
        }
      },
      {
        box: {
          color: "#FEDBDB",
          positionX: domainWidth / 2,
          positionY: 0,
          width: domainWidth / 2,
          height: domainHeight / 2
        },
        text: {
          label: "PM",
          positionX: (domainWidth * 3) / 4,
          positionY: (domainHeight * 1) / 4 + 12
        }
      },
      {
        box: {
          color: "#FEF9DA",
          positionX: 0,
          positionY: domainHeight / 2,
          width: domainWidth / 2,
          height: domainHeight / 2
        },
        text: {
          label: "pm",
          positionX: (domainWidth * 1) / 4,
          positionY: (domainHeight * 3) / 4 + 12
        }
      },
      {
        box: {
          color: "#DDF0F0",
          positionX: domainWidth / 2,
          positionY: domainHeight / 2,
          width: domainWidth / 2,
          height: domainHeight / 2
        },
        text: {
          label: "pM",
          positionX: (domainWidth * 3) / 4,
          positionY: (domainHeight * 3) / 4 + 12
        }
      }
    ];

    // defined scaleLinear
    this.scaleLinearX = scaleLinear()
      .domain([minScale, maxScale])
      .range([0, domainWidth]);
    this.scaleLinearY = scaleLinear()
      .domain([minScale, maxScale])
      .range([domainHeight, 0]);

    // set ticks
    const xAxis = axisBottom(this.scaleLinearX)
      .ticks(8)
      .tickSize(-domainHeight);
    const yAxis = axisLeft(this.scaleLinearY)
      .ticks(8)
      .tickSize(-domainWidth);
    const bottomScale = axisBottom(this.scaleLinearX)
      .tickSizeOuter(0)
      .ticks(0);
    const leftScale = axisLeft(this.scaleLinearY)
      .tickSizeOuter(0)
      .ticks(0);

    // create svg object
    this.svg = select(this.table)
      .append("svg")
      .attr("width", width)
      .attr("height", height)
      .append("g")
      .attr("transform", "translate(" + margin.left + "," + margin.top + ")");

    // draw each rect
    this.svg
      .selectAll("g.quadrants")
      .data(conditions.map(c => c.box))
      .enter()
      .append("g")
      .attr("class", "quadrant")
      .append("rect")
      .attr("x", d => d.positionX)
      .attr("y", d => d.positionY)
      .attr("width", d => d.width)
      .attr("height", d => d.height)
      .attr("fill", d => d.color)
      .style("opacity", 0.3);

    // draw text in each rect
    this.svg
      .selectAll("g.quadrants")
      .data(conditions.map(c => c.text))
      .enter()
      .append("g")
      .attr("class", "quadrant")
      .append("text")
      .attr("x", d => d.positionX)
      .attr("y", d => d.positionY)
      .text(d => d.label)
      .attr("font-size", "48px")
      .attr("fill", "#CCC")
      .attr("text-anchor", "middle")
      .attr("font-weight", "bold");

    // bottom axis
    this.svg
      .append("g")
      .attr("class", "axisBootom")
      .attr("transform", "translate(0," + domainHeight + ")")
      .call(bottomScale);

    // left axis
    this.svg
      .append("g")
      .attr("class", "axisLeft")
      .attr("transform", "translate(0,0)")
      .call(leftScale);

    // drawing inside line function
    // eslint-disable-next-line
    function customAxis(g: any, axisType: any) {
      const s = g.selection ? g.selection() : g;
      g.call(axisType);
      s.select(".domain").remove();
      s.selectAll(".tick line")
        .attr("stroke", "#999")
        .filter(Number)
        .attr("stroke", "#e3e3e3")
        .attr("stroke-dasharray", "3, 3");
    }

    // x-axis line
    this.svg
      .append("g")
      .attr("class", "x axis")
      .attr("transform", "translate(0," + domainHeight + ")")
      .call(customAxis, xAxis)
      .selectAll("text")
      .style("text-anchor", "end")
      .attr("dx", ".5em")
      .attr("dy", "2em");

    // y-axis line
    this.svg
      .append("g")
      .attr("class", "y axis")
      .call(customAxis, yAxis)
      .selectAll("text")
      .style("text-anchor", "end")
      .attr("dx", "-1.2em")
      .attr("dy", ".5em");

    // cross line of center
    this.svg
      .selectAll("g.cross-line")
      .data(crossLine.map(d => d))
      .enter()
      .append("g")
      .attr("class", "cross-line")
      .append("line")
      .style("stroke", "red")
      .style("stroke-width", 1)
      .attr("stroke-dasharray", "5, 3")
      .attr("fill", "none")
      .attr("x1", d => d.x1)
      .attr("y1", d => d.y1)
      .attr("x2", d => d.x2)
      .attr("y2", d => d.y2);

    // bottom label
    this.svg
      .append("text")
      .attr("transform", "translate(" + domainWidth / 2 + "," + (domainHeight + margin.bottom / 2) + ")")
      .attr("fill", "black")
      .attr("text-anchor", "middle")
      .attr("font-size", "10pt")
      .attr("font-weight", "bold")
      .text("メンテナンス");

    // left label
    this.svg
      .append("text")
      .attr("transform", "translate(" + -(margin.left / 2) + "," + domainHeight / 2 + ")")
      .attr("writing-mode", "tb-rl")
      .attr("fill", "black")
      .attr("text-anchor", "middle")
      .attr("font-size", "10pt")
      .attr("font-weight", "bold")
      .text("パフォーマンス");
  }
  plot() {
    this.svg.selectAll("g.node").remove();
    if (this.chartData && this.chartData.length) {
      this.svg
        .selectAll("g.node")
        .data(this.chartData.map(d => d))
        .enter()
        .append("g")
        .attr("class", "node")
        .attr("transform", d => {
          d.maintenance = d.maintenance! < 20 ? 20 : d.maintenance! > 80 ? 80 : d.maintenance;
          d.performance = d.performance! < 20 ? 20 : d.performance! > 80 ? 80 : d.performance;
          return "translate(" + this.scaleLinearX(d.maintenance) + "," + this.scaleLinearY(d.performance) + ")";
        });

      // cricle
      this.svg
        .selectAll("g.node")
        .append("circle")
        .data(this.chartData.map(d => d))
        .attr("r", 6)
        .attr("class", "dot")
        .style("fill", function(d) {
          return d.id.match(/^APL#.+?#$/) ? "#ff6d00" : d.id.match(/^.+#retired#$/) ? "#4a4a4a" : "#2962ff";
        });

      // text
      this.svg
        .selectAll("g.node")
        .append("text")
        .data(this.chartData.map(d => d))
        .style("text-anchor", "start")
        .style("fill", function(d) {
          return d.id.match(/^APL#.+?#$/) ? "#ff6d00" : d.id.match(/^.+#retired#$/) ? "#4a4a4a" : "#2962ff";
        })
        .attr("dx", 10)
        .text(d => {
          return d.lastName.concat(" ", d.firstName);
        });
    }
  }
  updated() {
    this.plot();
  }
  mounted() {
    this.draw();
    this.plot();
  }
}
</script>
